'use client';

import { Accordion } from '@mv/web-ui';
import { useDataTags, useDataTagsFactory } from '@/modules/tracking/analytics/useDataTags';
import { useHash } from '@/modules/utils/useHash';
import Feedback from '@/payload/components/Feedback/Feedback';
import RichText, { type RichTextOptions } from '@/payload/components/RichText/RichText';
import type { Faq, FaqsGroup as FaqsGroupProps } from '@/payload/types';

type Props = Omit<FaqsGroupProps, 'items' | 'blockType'> & {
	blockType?: FaqsGroupProps['blockType'];
	faqs: Faq[];
	options?: RichTextOptions;
};

export default function FaqsGroup({ blockType = 'faqsGroup', id, configuration, faqs, options }: Props) {
	const hash = useHash();
	const { blockRef } = useDataTags(blockType, id);
	const { createDataTags } = useDataTagsFactory();

	const currentFaq = faqs.find((faq) => faq.slug === hash)?.id.toString();

	options = {
		...options,
		size: 'small',
	};

	return (
		<Accordion.Root
			data-component="FaqsGroupBlock"
			ref={blockRef}
			appearance="open-close-black"
			autoClose={configuration?.autoClose ?? false}
			defaultValue={currentFaq}
		>
			{faqs.map(({ id, title, content, slug }) => {
				const { blockRef, labelRef, contentRef } = createDataTags('faq', id);
				return (
					<Accordion.Item key={id} ref={blockRef} value={`${id}`}>
						<Accordion.Trigger as="h3" ref={labelRef} id={slug ?? undefined}>
							{title}
						</Accordion.Trigger>
						<Accordion.Content ref={contentRef}>
							<RichText content={content} options={options} />
							<Feedback dataTag={{ label: title, values: { faqId: id } }} />
						</Accordion.Content>
					</Accordion.Item>
				);
			})}
		</Accordion.Root>
	);
}
